/**
 * Session-level state: who am I, which workspace + branch + diagram am I editing.
 * Hydrated on app mount via the API.
 */
import i18n from "i18next";
import { create } from "zustand";
import { ApiError, api, type BranchSummary, type MeResponse } from "../api/client";
import { isDisplayMode, useDisplayMode } from "./displayMode";

/**
 * "Is `me` admin of `workspaceId`" — echte `workspace_members`-rol OF een
 * actieve super-admin-override (Settings → Wissel workspace) op déze
 * workspace. Backend-mirror van `resolveRole` in
 * `apps/worker/src/middleware/workspace.ts`. Gebruik dit i.p.v. losjes
 * `me.workspaces.find(...).role === "admin"` te herhalen — anders is een
 * super admin wél ingewisseld maar ziet admin-only UI niet (ontdekt
 * 2026-07-23 in `SettingsMenu.tsx`, hierheen verplaatst zodat andere
 * call-sites 'm niet opnieuw missen).
 */
export function isAdminOfWorkspace(me: MeResponse | null, workspaceId: string | null): boolean {
  if (!me || !workspaceId) return false;
  if (me.workspaces.find((w) => w.workspace_id === workspaceId)?.role === "admin") return true;
  return me.super_admin_override?.workspace_id === workspaceId;
}

export interface SessionState {
  me: MeResponse | null;
  workspaceId: string | null;
  /** Laatst-gekozen workpackage binnen deze workspace. Gepersisteerd
   *  in localStorage zodat een hard refresh of nieuwe tab terugkeert
   *  naar dezelfde WP — anders viel de gebruiker iedere keer terug op
   *  `workpackages[0]` (eerste alfabetisch). */
  workpackageId: string | null;
  branchId: string | null;
  /**
   * Alleen kijken: het lid heeft op het actieve workpackage leesrecht en geen
   * schrijfrecht (Gerald, 2026-10-17). De editor schrijft dan niets, ook niet
   * vanzelf (nasynchroniseren, viewState bewaren, sloten). Gezet door
   * `Editor.tsx` zodra het workpackage bekend is; tot dan `false`, de worker
   * weigert toch.
   */
  readOnly: boolean;
  setReadOnly: (readOnly: boolean) => void;
  loading: boolean;
  error: string | null;

  hydrate: () => Promise<void>;
  /** Wissel naar een andere workspace waarvan de gebruiker lid is. De keuze
   *  wordt globaal gepersisteerd zodat een refresh terugkomt in dezelfde
   *  workspace, en hydrate() opnieuw wordt gedraaid zodat branches en
   *  workpackages voor de nieuwe workspace geladen worden. */
  switchWorkspace: (workspaceId: string) => Promise<void>;
  /** Wissel naar een ander workpackage. Caller (Explorer) is verantwoordelijk
   *  voor het herladen van branches + tree na de switch. */
  setWorkpackageId: (workpackageId: string) => void;
  /** Wissel naar een andere branch binnen de huidige workspace.
   *  Caller (Editor) moet daarna `useWorkspaceData.hydrate()` aanroepen
   *  zodat de canvas/diagrammen herladen worden, en de CRDT-sessie
   *  aanpassen. */
  setBranchId: (branchId: string) => void;
  /** Verlaat een actieve super-admin-override (Settings → Wissel
   *  workspace) en re-hydrateert naar de eigen workspace(s). */
  exitSuperAdminOverride: () => Promise<void>;
  /** `me` verversen zonder laadscherm, bijvoorbeeld na een nieuwe of
   *  verwijderde workspace. Is de huidige workspace weg, dan volgt een
   *  volledige `hydrate()`. */
  refreshMe: () => Promise<void>;
}

// LocalStorage-keys: per workspace bewaren we de laatst-gekozen branch
// zodat een refresh of nieuwe browser-tab de gebruiker terugbrengt
// naar dezelfde branch — anders viel 'ie iedere keer terug naar main.
const STORAGE_KEY_BRANCH = "ae.session.branchId";
const STORAGE_KEY_WORKPACKAGE = "ae.session.workpackageId";
// De laatst-gekozen workspace staat globaal (niet per workspace) — anders
// heb je een kip-en-ei zodra iemand tussen twee workspaces heen en weer wil.
const STORAGE_KEY_WORKSPACE = "ae.session.workspaceId";

function readWorkspacePref(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY_WORKSPACE);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}
function writeWorkspacePref(value: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY_WORKSPACE, value);
    else window.localStorage.removeItem(STORAGE_KEY_WORKSPACE);
  } catch {
    // Storage may be unavailable; silently ignore.
  }
}

function readPersisted(key: string, workspaceId: string | null): string | null {
  if (!workspaceId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${key}:${workspaceId}`);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}
function writePersisted(key: string, workspaceId: string | null, value: string | null): void {
  if (!workspaceId || typeof window === "undefined") return;
  try {
    const k = `${key}:${workspaceId}`;
    if (value) window.localStorage.setItem(k, value);
    else window.localStorage.removeItem(k);
  } catch {
    // Storage may be unavailable; silently ignore.
  }
}

/**
 * Waar een workspace bij de eerstvolgende `hydrate` opent: workpackage en
 * branch. Voor een workspace die net is aangemaakt (bijvoorbeeld het
 * voorbeeld), zodat de app niet in Base begint.
 */
export function rememberWorkpackage(
  workspaceId: string,
  workpackageId: string,
  branchId: string,
): void {
  writePersisted(STORAGE_KEY_WORKPACKAGE, workspaceId, workpackageId);
  writePersisted(STORAGE_KEY_BRANCH, workspaceId, branchId);
}

export const useSession = create<SessionState>((set, get) => ({
  me: null,
  workspaceId: null,
  workpackageId: null,
  branchId: null,
  readOnly: false,
  loading: false,
  error: null,

  setReadOnly: (readOnly: boolean) => {
    if (get().readOnly !== readOnly) set({ readOnly });
  },

  setWorkpackageId: (workpackageId: string) => {
    writePersisted(STORAGE_KEY_WORKPACKAGE, get().workspaceId, workpackageId);
    set({ workpackageId });
  },
  setBranchId: (branchId: string) => {
    writePersisted(STORAGE_KEY_BRANCH, get().workspaceId, branchId);
    set({ branchId });
  },
  exitSuperAdminOverride: async () => {
    await api.exitSuperAdminOverride();
    await get().hydrate();
  },
  refreshMe: async () => {
    const me = await api.me();
    const current = get().workspaceId;
    const stillThere =
      !current ||
      me.workspaces.some((w) => w.workspace_id === current) ||
      me.super_admin_override?.workspace_id === current;
    if (!stillThere) {
      await get().hydrate();
      return;
    }
    set({ me });
  },
  switchWorkspace: async (workspaceId: string) => {
    const state = get();
    if (state.workspaceId === workspaceId) return;
    // Membership-check op de client vermijdt een zinloze hydrate — de
    // dropdown toont alleen workspaces waar 'ie lid van is, dus dit is
    // een defensief vangnet voor programmatische aanroepen.
    if (!state.me?.workspaces.some((w) => w.workspace_id === workspaceId)) return;
    writeWorkspacePref(workspaceId);
    // De branch/workpackage van de vorige workspace wissen zodat hydrate()
    // niet een halve seconde met een stale branchId werkt.
    set({ branchId: null, workpackageId: null });
    await get().hydrate();
  },

  hydrate: async () => {
    set({ loading: true, error: null });
    try {
      const me = await api.me();
      // Apply the user's stored ui_language preference, but only when it
      // differs from the current i18next language — otherwise we trigger
      // a needless re-render. Server-side default is "nl"; respect that
      // even if the browser-default detected "en" first.
      if (me.ui_language && me.ui_language !== i18n.language) {
        void i18n.changeLanguage(me.ui_language);
      }
      // Idem voor de ArchiMate-weergavemodus — zelfde soort per-user-
      // voorkeur, alleen leest die z'n eigen store i.p.v. i18next.
      if (isDisplayMode(me.display_mode)) {
        useDisplayMode.getState().seed(me.display_mode);
      }
      // Een actieve super-admin-override (Settings → Wissel workspace)
      // wint van de normale membership-lijst — anders zou een refresh
      // de super admin meteen terugklappen naar z'n eigen workspace(s).
      // Daarna geldt: een gepersisteerde workspace-voorkeur die nog
      // een geldige membership is, wint van de eerste workspace-in-lijst,
      // zodat switchWorkspace() een refresh overleeft.
      const persistedWs = readWorkspacePref();
      const persistedMembership = persistedWs
        ? me.workspaces.find((w) => w.workspace_id === persistedWs)
        : null;
      const workspaceId =
        me.super_admin_override?.workspace_id ??
        persistedMembership?.workspace_id ??
        me.workspaces[0]?.workspace_id;
      if (!workspaceId) {
        // Een super admin kan een pure "helper"-account zijn zonder eigen
        // workspace-membership — dat is GEEN foutstate (anders kan die
        // nooit bij Settings → Wissel workspace komen om er een te
        // kiezen). Alleen echte "geen workspaces bestaan"-installaties
        // (geen super admin) blijven de harde foutmelding tonen.
        set({
          me,
          workspaceId: null,
          loading: false,
          error: me.is_super_admin
            ? null
            : "No workspace available. Run pnpm bootstrap:local to create one.",
        });
        return;
      }
      const branches = await api.listBranches(workspaceId);
      const main = branches.find((b: BranchSummary) => b.is_main) ?? branches[0];
      if (!main) {
        set({
          me,
          workspaceId,
          loading: false,
          error: "Workspace has no branches.",
        });
        return;
      }
      // Restore de laatst-gekozen branch uit localStorage; valideer
      // dat 'ie nog bestaat (bv. niet verwijderd via Settings).
      // Anders fallback naar main.
      const persistedBranchId = readPersisted(STORAGE_KEY_BRANCH, workspaceId);
      const persistedBranch = persistedBranchId
        ? branches.find((b) => b.id === persistedBranchId)
        : null;
      const initialBranch = persistedBranch ?? main;
      // Workpackage-id wordt zonder verify gerestoreerd — de Explorer
      // valideert later tegen `listWorkpackages` en valt terug op
      // `workpackages[0]` als de gepersisteerde WP niet meer bestaat.
      const persistedWpId = readPersisted(STORAGE_KEY_WORKPACKAGE, workspaceId);
      set({
        me,
        workspaceId,
        workpackageId: persistedWpId,
        branchId: initialBranch.id,
        loading: false,
      });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      set({ loading: false, error: message });
    }
  },
}));
